/**
 * HistoricoPage — Reservas completadas, canceladas, no-show o suspendidas.
 * Vista analítica con: rango de fechas, filtro por agencia, chips de estado
 * multi-select con contadores y KPI bar.
 *
 * Sólo lectura — el SlideOver permite ver detalle sin editar.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, TextField, MenuItem, Button, Chip, Stack,
  FormControl, InputLabel, Select, IconButton, Collapse, InputAdornment,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PaidIcon from '@mui/icons-material/Paid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReservacionesTable from './shared/ReservacionesTable';
import ReservaSlideOver from './ReservaSlideOver';
import HistorialReservaModal from './HistorialReservaModal';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useHistoricoReservaciones } from './shared/useHistoricoReservaciones';
import { ESTADO_LABELS } from './shared/EstadoChip';
import { todayLocal, API_BASE, getEmpresaId, getAuthHeaders, formatPrecio, norm } from './shared/format';
import { exportarReservasCSV } from './shared/exportCSV';

// Estados válidos en histórico. Los cuatro primeros son los estados finales;
// los demás son reservas de pasadías ya realizados que quedaron abiertas. Antes
// no estaban acá y no había forma de verlas: el histórico decía «no hay
// reservas» para agencias que sí tenían. El rango de fechas define el
// histórico; el estado es un filtro, no un recorte invisible.
const ESTADOS_HIST = ['completada', 'cancelada', 'no_show', 'suspendida',
                      'confirmada', 'hold', 'pendiente', 'embarcada'];

// `estado='embarcada'` es el estado del CHARTER en operación, no el embarque
// de un pasadía: un pasadía que embarcó queda en `completada` y su embarque
// vive en `estado_embarque`. Sin esta aclaración el chip se lee como «los
// embarcados» y devuelve una sola fila de 1.200. Quien busque embarques
// reales va a Reportes → Embarques por rango.
const ETIQUETA_HIST = { embarcada: 'Embarcada (charter)' };
const etiquetaEstado = (est) =>
  ETIQUETA_HIST[est] || (ESTADO_LABELS[est]?.label ?? est);

// Presets de rango de fechas (YYYY-MM-DD locales).
function _localStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function _restar(dias) {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return _localStr(d);
}
function _inicioMes(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return _localStr(d);
}
function _finMes(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset + 1);
  d.setDate(0);
  return _localStr(d);
}

const PRESETS = [
  { id: 'hoy',        label: 'Hoy',           desde: () => todayLocal(),  hasta: () => todayLocal() },
  { id: 'semana',     label: 'Últimos 7 días', desde: () => _restar(6),    hasta: () => todayLocal() },
  { id: 'mes',        label: 'Este mes',      desde: () => _inicioMes(0), hasta: () => _finMes(0) },
  { id: 'mes_pasado', label: 'Mes pasado',    desde: () => _inicioMes(-1), hasta: () => _finMes(-1) },
  { id: '3meses',     label: 'Últimos 3 meses', desde: () => _restar(89), hasta: () => todayLocal() },
  { id: 'todo',       label: 'Todo',          desde: () => '',            hasta: () => '' },
];

// Etiqueta legible de un mes YYYY-MM (ej. "Junio 2026").
const MESES_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
function _labelMes(yyyy_mm) {
  if (!yyyy_mm || !/^\d{4}-\d{2}$/.test(yyyy_mm)) return yyyy_mm;
  const [y, m] = yyyy_mm.split('-');
  return `${MESES_ES[parseInt(m, 10) - 1]} ${y}`;
}

/**
 * Agrupa reservas por mes de salida (YYYY-MM). Devuelve un array ordenado
 * por mes descendente (más reciente primero), cada bucket con sus KPIs
 * pre-calculados y reservas ordenadas por fecha desc dentro del mes.
 */
function agruparPorMes(rows) {
  const buckets = new Map();
  for (const r of rows) {
    const key = String(r.salida_fecha || '').slice(0, 7) || 'sin-fecha';
    if (!buckets.has(key)) {
      buckets.set(key, {
        mes: key,
        label: key === 'sin-fecha' ? 'Sin fecha' : _labelMes(key),
        reservas: [],
      });
    }
    buckets.get(key).reservas.push(r);
  }
  const arr = Array.from(buckets.values());
  arr.sort((a, b) => b.mes.localeCompare(a.mes));
  for (const g of arr) {
    g.reservas.sort((a, b) => String(b.salida_fecha || '').localeCompare(String(a.salida_fecha || '')));
    const numCompl = g.reservas.filter(r => r.estado === 'completada').length;
    const facturado = g.reservas.reduce((s, r) => {
      const neto = Math.max(Number(r.precio_total || 0) - Number(r.comision_valor || 0), 0);
      return s + neto;
    }, 0);
    g.kpis = { num: g.reservas.length, numCompl, facturado };
  }
  return arr;
}

// Colores de estados para la timeline visual (coinciden con EstadoChip)
const TIMELINE_COLORS = {
  completada: '#059669',
  cancelada:  '#dc2626',
  no_show:    '#7f1d1d',
  suspendida: '#eab308',
  confirmada: '#1e40af',
  hold:       '#86198f',
  pendiente:  '#92400e',
  embarcada:  '#0f766e',
};

/**
 * TimelineMensual — barras horizontales apiladas por estado, una por mes.
 * Ancho relativo al mes con más reservas del rango. Click en una barra
 * togglea la expansión del grupo mensual correspondiente en la lista.
 *
 * Se muestra solo cuando hay 2+ meses (con 1 no aporta comparación).
 */
function TimelineMensual({ grupos, onClickMes }) {
  const maxReservas = React.useMemo(
    () => grupos.reduce((m, g) => Math.max(m, g.reservas.length), 0),
    [grupos]
  );
  if (grupos.length === 0) return null;
  return (
    <Box>
      <Stack spacing={0.6}>
        {grupos.map(g => {
          const conteo = { completada: 0, cancelada: 0, no_show: 0, suspendida: 0 };
          for (const r of g.reservas) if (r.estado in conteo) conteo[r.estado] += 1;
          const total = g.reservas.length;
          const widthPct = maxReservas > 0 ? (total / maxReservas) * 100 : 0;
          return (
            <Box key={g.mes}
              onClick={() => onClickMes?.(g.mes)}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '110px 1fr 40px', sm: '140px 1fr 50px' },
                alignItems: 'center', gap: 1, cursor: 'pointer',
                p: 0.5, borderRadius: 1,
                transition: 'background 0.12s',
                '&:hover': { bgcolor: '#f8fafc' },
              }}>
              <Typography sx={{
                fontSize: 12, fontWeight: 700, color: '#0f172a', minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {g.label}
              </Typography>
              <Box sx={{
                height: 22, bgcolor: '#f1f5f9', borderRadius: 1, overflow: 'hidden',
                position: 'relative',
              }}>
                <Box sx={{
                  width: `${widthPct}%`, height: '100%',
                  display: 'flex', transition: 'width 0.2s',
                }}>
                  {ESTADOS_HIST.map(est => {
                    const n = conteo[est] || 0;
                    if (n === 0) return null;
                    return (
                      <Box key={est}
                        title={`${etiquetaEstado(est)}: ${n}`}
                        sx={{
                          flex: n,
                          bgcolor: TIMELINE_COLORS[est],
                          transition: 'opacity 0.15s',
                          '&:hover': { opacity: 0.8 },
                          // Etiqueta interna cuando el segmento es "gordo"
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 10, fontWeight: 700,
                          overflow: 'hidden',
                        }}>
                        {(n / total) > 0.15 ? n : ''}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Typography sx={{
                fontSize: 12, fontWeight: 800, color: '#0f172a',
                textAlign: 'right',
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {total}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Leyenda de colores */}
      <Box sx={{ mt: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap', pl: 0.5 }}>
        {ESTADOS_HIST.map(est => (
          <Box key={est} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: TIMELINE_COLORS[est], borderRadius: 0.3 }} />
            <Typography sx={{ fontSize: 10.5, color: '#475569', fontWeight: 600 }}>
              {etiquetaEstado(est)}
            </Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: 10.5, color: '#94a3b8', fontStyle: 'italic', ml: 'auto' }}>
          Click en una barra para expandir el mes ↓
        </Typography>
      </Box>
    </Box>
  );
}

// Estilo consistente para KPI tiles (mismo patrón que HoyKpiBar)
function KpiTile({ label, value, sub, icon, color = '#0891b2' }) {
  return (
    <Box sx={{
      position: 'relative',
      p: '10px 14px',
      bgcolor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.3,
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 2, background: color, opacity: 0.85,
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 14, color } })}
        <Typography sx={{
          fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase',
          fontWeight: 700, color: '#475569',
        }}>{label}</Typography>
      </Box>
      <Typography sx={{
        fontSize: 18, fontWeight: 800, color: '#0f172a',
        lineHeight: 1.15, letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      }}>{value}</Typography>
      {sub && (
        <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>{sub}</Typography>
      )}
    </Box>
  );
}

export default function HistoricoPage() {
  // El outlet context sigue disponible por si otras acciones lo necesitan
  // (ej. el SlideOver invoca refetch tras editar). Ya no consumimos las
  // reservaciones globales — el histórico se pide directo al endpoint dedicado.
  const { refetch: refetchGlobal } = useOutletContext();
  const empresaId = getEmpresaId();
  const [detalleId, setDetalleId] = useState(null);
  // Modal de snapshot histórico — se abre con el `codigo_reserva` de la fila.
  // Muestra creación, snapshot original, timeline, pagos y vouchers.
  const [historialCodigo, setHistorialCodigo] = useState(null);
  // Preset por defecto: Este mes (foco en el mes en curso)
  const [presetId, setPresetId] = useState('mes');
  const [fechaDesde, setFechaDesde] = useState(() => _inicioMes(0));
  const [fechaHasta, setFechaHasta] = useState(() => _finMes(0));
  const [agenciaFiltro, setAgenciaFiltro] = useState('todas');
  const [estadosActivos, setEstadosActivos] = useState(new Set(ESTADOS_HIST));   // todos por default
  const [agenciasList, setAgenciasList] = useState([]);
  // Búsqueda global (subida al header — reemplaza el buscador local de cada
  // tabla mensual, que ahora va oculto con hideSearch).
  // `busqueda` es el texto crudo del input. `busquedaDebounced` es el que
  // se manda al backend (200ms de gracia para no disparar un fetch por tecla).
  // `busquedaNorm` alimenta el filtro cliente-side (útil cuando el operador
  // teclea muy rápido y el debounce aún no disparó).
  const [busqueda, setBusqueda] = useState('');
  const [busquedaDebounced, setBusquedaDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusquedaDebounced(busqueda.trim()), 250);
    return () => clearTimeout(t);
  }, [busqueda]);
  const busquedaNorm = norm(busqueda.trim());

  // Cargar lista de agencias una sola vez
  useEffect(() => {
    fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const arr = Array.isArray(list) ? list : (list?.agencias || []);
        setAgenciasList([...arr].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
      })
      .catch(() => setAgenciasList([]));
  }, [empresaId]);

  // Fetch server-side del histórico — reactivo a rango, agencia y estados.
  // La búsqueda global sigue siendo client-side (sobre el subset del rango).
  const {
    reservaciones: historico,
    total: historicoTotal,
    truncated,
    rangoEfectivo,
    isLoading,
    isFetching,
    refetch: refetchHistorico,
  } = useHistoricoReservaciones({
    desde: fechaDesde || undefined,
    hasta: fechaHasta || undefined,
    agenciaId: agenciaFiltro,
    estados: Array.from(estadosActivos),
    // Búsqueda por texto: cuando viene con ≥2 chars, el backend ignora las
    // fechas y busca en toda la BD por código/cliente/whatsapp/correo/doc.
    q: busquedaDebounced,
  });

  // Cuando el SlideOver actualiza una reserva, refresh también el histórico
  // (además del refetch global que refresca las vistas activas).
  const refetch = () => {
    refetchGlobal?.();
    refetchHistorico();
  };

  // Aplicar preset → actualiza fechas
  const aplicarPreset = (id) => {
    setPresetId(id);
    const p = PRESETS.find(x => x.id === id);
    if (!p) return;
    setFechaDesde(p.desde());
    setFechaHasta(p.hasta());
  };

  // Cuando el usuario edita manualmente una fecha, sale del preset ("Personalizado")
  const onFechaManual = (setter) => (val) => {
    setter(val);
    setPresetId('custom');
  };

  const toggleEstado = (est) => {
    setEstadosActivos(prev => {
      const s = new Set(prev);
      if (s.has(est)) s.delete(est); else s.add(est);
      return s;
    });
  };

  // `historico` ya viene filtrado del backend por rango + agencia + estados.
  // Solo queda aplicar la búsqueda global client-side (evita un round-trip
  // por cada tecla y aprovecha que el subset del rango es acotado).
  const filtradasBase = historico;

  // Conteos por estado dentro del subset con búsqueda (para los chips).
  // NOTA: como los estados también van al backend, el conteo aquí refleja
  // "cuántas hay del estado X que además ya pasaron el filtro backend".
  // Si el operador desactiva un estado en los chips, se re-consulta al
  // backend y los conteos se re-calculan.
  const conteosEstado = useMemo(() => {
    const out = Object.fromEntries(ESTADOS_HIST.map(e => [e, 0]));
    const base = busquedaNorm
      ? filtradasBase.filter(r => (
          norm(r.codigo_reserva).includes(busquedaNorm) ||
          norm(r.nombre_cliente).includes(busquedaNorm) ||
          norm(r.numero_whatsapp).includes(busquedaNorm) ||
          norm(r.correo).includes(busquedaNorm) ||
          norm(r.agencia_nombre).includes(busquedaNorm) ||
          norm(r.paquete_nombre).includes(busquedaNorm)
        ))
      : filtradasBase;
    for (const r of base) {
      if (r.estado in out) out[r.estado] += 1;
    }
    return out;
  }, [filtradasBase, busquedaNorm]);

  // Filtro final: solo aplica la búsqueda (fecha/agencia/estados ya se
  // filtraron server-side).
  const filtered = useMemo(() => {
    if (!busquedaNorm) return filtradasBase;
    return filtradasBase.filter(r => (
      norm(r.codigo_reserva).includes(busquedaNorm) ||
      norm(r.nombre_cliente).includes(busquedaNorm) ||
      norm(r.numero_whatsapp).includes(busquedaNorm) ||
      norm(r.correo).includes(busquedaNorm) ||
      norm(r.agencia_nombre).includes(busquedaNorm) ||
      norm(r.paquete_nombre).includes(busquedaNorm)
    ));
  }, [filtradasBase, busquedaNorm]);

  // Agrupación por mes (más reciente primero)
  const grupos = useMemo(() => agruparPorMes(filtered), [filtered]);

  // Estado de expansión por mes. Reglas:
  //   · Sin búsqueda → solo el mes más reciente expandido.
  //   · Con búsqueda → todos los meses con resultados expandidos (el operador
  //     no debería tener que abrir manualmente cada grupo para ver hits).
  const [mesesExpandidos, setMesesExpandidos] = useState(() => new Set());
  useEffect(() => {
    if (grupos.length === 0) {
      setMesesExpandidos(new Set());
    } else if (busquedaNorm) {
      setMesesExpandidos(new Set(grupos.map(g => g.mes)));
    } else {
      setMesesExpandidos(new Set([grupos[0].mes]));
    }
    // Solo re-init cuando cambia la LISTA de meses o el estado de búsqueda
    // (no cuando cambian los rows dentro del mismo mes por otro filtro).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupos.map(g => g.mes).join('|'), busquedaNorm]);

  const toggleMes = (mes) => {
    setMesesExpandidos(prev => {
      const s = new Set(prev);
      if (s.has(mes)) s.delete(mes); else s.add(mes);
      return s;
    });
  };
  const expandirTodos = () => setMesesExpandidos(new Set(grupos.map(g => g.mes)));
  const colapsarTodos = () => setMesesExpandidos(new Set());
  const todosExpandidos = grupos.length > 0 && mesesExpandidos.size === grupos.length;

  // Timeline visual — colapsable, default expandido cuando hay 2+ meses
  const [timelineAbierto, setTimelineAbierto] = useState(true);

  // KPIs del subset filtrado (con estados aplicados)
  const kpis = useMemo(() => {
    const num = filtered.length;
    const numCompl = filtered.filter(r => r.estado === 'completada').length;
    // Facturado neto = precio_total − comision_valor (misma fórmula que ya usa el sistema)
    const facturado = filtered.reduce((s, r) => {
      const neto = Math.max(Number(r.precio_total || 0) - Number(r.comision_valor || 0), 0);
      return s + neto;
    }, 0);
    // Ticket promedio: neto por reserva completada (ignora canceladas/no-show)
    const complets = filtered.filter(r => r.estado === 'completada');
    const netoCompl = complets.reduce((s, r) => {
      const neto = Math.max(Number(r.precio_total || 0) - Number(r.comision_valor || 0), 0);
      return s + neto;
    }, 0);
    const ticket = complets.length > 0 ? netoCompl / complets.length : 0;
    return { num, numCompl, facturado, ticket };
  }, [filtered]);

  const rangoTexto = useMemo(() => {
    if (!fechaDesde && !fechaHasta) return 'todo el histórico';
    if (fechaDesde === fechaHasta) return fechaDesde;
    return `${fechaDesde || '—'} → ${fechaHasta || '—'}`;
  }, [fechaDesde, fechaHasta]);

  return (
    <Box>
      {/* Header con búsqueda global y CSV */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
            Histórico
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            {historicoTotal} en el rango
            {historicoTotal !== filtered.length && (
              <> · mostrando <b>{filtered.length}</b></>
            )}
            {busquedaNorm ? <> · buscando <b>"{busqueda.trim()}"</b></> : <> ({rangoTexto})</>}
            {isFetching && !isLoading && <> · <em>actualizando…</em></>}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Búsqueda local eliminada — la barra global (BookingLayout) ya
              cubre búsqueda por texto en todas las secciones. */}
          {/* Botón para consultar el snapshot histórico de cualquier reserva
              pegando su código. Es el atajo más rápido cuando el operador
              tiene el código en un WhatsApp o en un ticket. */}
          <Button variant="outlined" startIcon={<EventNoteIcon />} size="small"
            onClick={() => {
              const cod = window.prompt('Código de reserva (ej. ENT-20260716-015):');
              if (cod && cod.trim()) setHistorialCodigo(cod.trim().toUpperCase());
            }}
            sx={{ textTransform: 'none' }}>
            Ver historial
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} size="small"
            disabled={filtered.length === 0}
            onClick={() => exportarReservasCSV(filtered, `reservas-historico-${todayLocal()}.csv`)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#0f766e', borderColor: '#0f766e' }}>
            CSV ({filtered.length})
          </Button>
        </Box>
      </Box>

      {/* KPI bar */}
      <Box sx={{
        display: 'grid',
        gap: 1,
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        mb: 1.5,
      }}>
        <KpiTile label="Reservas" value={kpis.num}
          sub={`${kpis.numCompl} completadas`}
          icon={<ReceiptLongIcon />} color="#0891b2" />
        <KpiTile label="Completadas" value={kpis.numCompl}
          sub={kpis.num > 0 ? `${((kpis.numCompl / kpis.num) * 100).toFixed(0)}% del total` : '—'}
          icon={<CheckCircleIcon />} color="#059669" />
        <KpiTile label="Facturado neto" value={formatPrecio(kpis.facturado)}
          sub="Suma de netos Entretur"
          icon={<PaidIcon />} color="#d97706" />
        <KpiTile label="Ticket promedio" value={formatPrecio(kpis.ticket)}
          sub="Neto / reserva completada"
          icon={<PaidIcon />} color="#7c3aed" />
      </Box>

      {/* Timeline visual mensual — visible con 1+ meses en el rango.
          Click en una barra togglea la expansión del grupo mensual. */}
      {grupos.length >= 1 && (
        <Box sx={{
          mb: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}>
          <Box
            onClick={() => setTimelineAbierto(v => !v)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              p: 1.25, cursor: 'pointer', userSelect: 'none',
              bgcolor: timelineAbierto ? '#f0fdfa' : '#f8fafc',
              transition: 'background 0.15s',
              '&:hover': { bgcolor: timelineAbierto ? '#ccfbf1' : '#eef2f6' },
            }}>
            <BarChartIcon sx={{ fontSize: 18, color: '#0d9488' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a',
                              textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Timeline mensual
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b', ml: 0.5 }}>
              ({grupos.length} {grupos.length === 1 ? 'mes' : 'meses'})
            </Typography>
            <IconButton size="small" sx={{
              ml: 'auto',
              transition: 'transform 0.18s',
              transform: timelineAbierto ? 'rotate(0deg)' : 'rotate(-90deg)',
              color: '#0d9488',
            }}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>
          <Collapse in={timelineAbierto} timeout={200} unmountOnExit>
            <Box sx={{ p: 1.25, pt: 0.5, borderTop: '1px solid #99f6e4' }}>
              <TimelineMensual grupos={grupos} onClickMes={toggleMes} />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Aviso: rango cortado por el techo de 12 meses (backend) */}
      {truncated && rangoEfectivo && (
        <Box sx={{
          mb: 1.5, p: 1, bgcolor: '#fef3c7', border: '1px solid #fcd34d',
          borderRadius: 2, display: 'flex', gap: 1, alignItems: 'center',
        }}>
          <Typography sx={{ fontSize: 12, color: '#92400e' }}>
            Mostrando los <b>últimos 12 meses</b> ({rangoEfectivo.desde} → {rangoEfectivo.hasta}).
            Para ver reservas más antiguas, definí un rango explícito en los filtros.
          </Typography>
        </Box>
      )}

      {/* Filtros: búsqueda + rango de fechas + presets + agencia */}
      <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
        {/* Fila 0: buscador — cuando el operador escribe ≥2 chars, el backend
            ignora el rango de fechas y busca en TODA la BD por código,
            cliente, whatsapp, correo o documento. */}
        <Box sx={{ mb: 1.25 }}>
          <TextField
            fullWidth size="small"
            placeholder="Buscar por código, cliente, WhatsApp, correo o documento… (mín. 2 caracteres, ignora rango de fechas)"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#94a3b8', mr: 1 }} />,
              endAdornment: busqueda && (
                <IconButton size="small" onClick={() => setBusqueda('')} title="Limpiar búsqueda">
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ),
              sx: { bgcolor: '#f8fafc' },
            }}
          />
          {busquedaDebounced && busquedaDebounced.length >= 2 && (
            <Typography sx={{ fontSize: 10.5, color: '#0891b2', mt: 0.5, fontStyle: 'italic' }}>
              🔍 Buscando "<b>{busquedaDebounced}</b>" en toda la BD (rango de fechas ignorado).
            </Typography>
          )}
        </Box>

        {/* Fila 1: presets de fecha */}
        <Box sx={{
          display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.25,
          alignItems: 'center',
        }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mr: 0.5,
                            textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Rango:
          </Typography>
          {PRESETS.map(p => {
            const active = presetId === p.id;
            return (
              <Chip key={p.id} size="small" clickable
                label={p.label}
                onClick={() => aplicarPreset(p.id)}
                sx={{
                  bgcolor: active ? '#0891b2' : '#f8fafc',
                  color: active ? '#fff' : '#475569',
                  border: `1px solid ${active ? '#0e7490' : '#e2e8f0'}`,
                  fontWeight: 600, fontSize: 11,
                  '&:hover': { bgcolor: active ? '#0e7490' : '#eef2f6' },
                }}
              />
            );
          })}
          {presetId === 'custom' && (
            <Chip size="small" label="Personalizado"
              sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: 11 }} />
          )}
        </Box>

        {/* Fila 2: fechas manuales + agencia */}
        <Box sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr) 1.2fr', md: 'repeat(2, 160px) 1fr' },
          alignItems: 'center',
        }}>
          <TextField size="small" type="date" label="Desde"
            InputLabelProps={{ shrink: true }}
            value={fechaDesde}
            onChange={e => onFechaManual(setFechaDesde)(e.target.value)}
            sx={{ bgcolor: '#fff' }} />
          <TextField size="small" type="date" label="Hasta"
            InputLabelProps={{ shrink: true }}
            value={fechaHasta}
            onChange={e => onFechaManual(setFechaHasta)(e.target.value)}
            sx={{ bgcolor: '#fff' }} />
          <FormControl size="small" sx={{ bgcolor: '#fff' }}>
            <InputLabel>Agencia</InputLabel>
            <Select value={agenciaFiltro} label="Agencia"
              onChange={e => setAgenciaFiltro(e.target.value)}>
              <MenuItem value="todas"><em>Todas las agencias</em></MenuItem>
              {agenciasList.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Chips de estado multi-select con contadores. Los contadores reflejan
          el subset filtrado por fecha/agencia (no el subset final con los
          estados aplicados), así el operador ve cuántas hay de cada estado. */}
      <Box sx={{
        mb: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mr: 0.5,
                          textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Estados:
        </Typography>
        {ESTADOS_HIST.map(est => {
          const cfg = ESTADO_LABELS[est] || { label: est, bg: '#e2e8f0', color: '#475569' };
          const active = estadosActivos.has(est);
          const n = conteosEstado[est] || 0;
          return (
            <Chip key={est} size="small" clickable
              label={
                <span>
                  {etiquetaEstado(est)}
                  <Box component="span" sx={{
                    ml: 0.6,
                    color: active ? cfg.color : '#94a3b8',
                    fontWeight: 700, opacity: active ? 1 : 0.7,
                  }}>{n}</Box>
                </span>
              }
              onClick={() => toggleEstado(est)}
              sx={{
                bgcolor: active ? cfg.bg : '#f8fafc',
                color: active ? cfg.color : '#94a3b8',
                border: `1px solid ${active ? cfg.color : '#e2e8f0'}`,
                fontWeight: 600, fontSize: 11,
                '&:hover': { bgcolor: active ? cfg.bg : '#eef2f6' },
              }}
              icon={
                active
                  ? <CheckCircleIcon sx={{ fontSize: '14px !important', color: `${cfg.color} !important` }} />
                  : undefined
              }
            />
          );
        })}
        {/* Toggle rápido: todos / ninguno */}
        <Button size="small"
          onClick={() => setEstadosActivos(
            estadosActivos.size === ESTADOS_HIST.length ? new Set() : new Set(ESTADOS_HIST)
          )}
          sx={{ textTransform: 'none', fontSize: 11, ml: 0.5, color: '#0891b2' }}>
          {estadosActivos.size === ESTADOS_HIST.length ? 'Ninguno' : 'Todos'}
        </Button>

        {/* Expandir/colapsar todos los meses — solo visible si hay 2+ grupos */}
        {grupos.length > 1 && (
          <Button size="small"
            startIcon={todosExpandidos
              ? <UnfoldLessIcon sx={{ fontSize: 14 }} />
              : <UnfoldMoreIcon sx={{ fontSize: 14 }} />}
            onClick={() => (todosExpandidos ? colapsarTodos() : expandirTodos())}
            sx={{ textTransform: 'none', fontSize: 11, ml: 'auto', color: '#0891b2' }}>
            {todosExpandidos ? 'Colapsar todos' : 'Expandir todos'}
          </Button>
        )}
      </Box>

      {/* Grupos por mes (colapsables). Cada mes tiene su header con KPIs
          y una tabla adentro (sin buscador — el buscador global sube al
          punto 6 del roadmap). */}
      {grupos.length === 0 ? (
        <Box sx={{
          p: 4, textAlign: 'center', bgcolor: '#fff',
          border: '1px dashed #cbd5e1', borderRadius: 2,
        }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
            {filtradasBase.length === 0
              ? 'Sin reservas en el rango'
              : busquedaNorm
                ? 'Sin coincidencias'
                : 'Sin resultados'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            {filtradasBase.length === 0
              ? 'No hay reservas históricas en el rango de fechas y agencia seleccionados. Ajustá los filtros.'
              : busquedaNorm
                ? `Ninguna reserva coincide con "${busqueda.trim()}" en el rango y estados seleccionados.`
                : estadosActivos.size === 0
                  ? 'Ningún estado seleccionado — activá al menos uno arriba.'
                  : 'Ninguna reserva coincide con los estados seleccionados.'}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {grupos.map(g => {
            const expandido = mesesExpandidos.has(g.mes);
            return (
              <Box key={g.mes} sx={{
                bgcolor: '#fff', border: '1px solid #e2e8f0',
                borderRadius: 2, overflow: 'hidden',
              }}>
                {/* Header del mes — click togglea */}
                <Box onClick={() => toggleMes(g.mes)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'auto 1fr auto auto auto' },
                    alignItems: 'center', gap: { xs: 1, sm: 2 },
                    p: 1.25, cursor: 'pointer', userSelect: 'none',
                    bgcolor: expandido ? '#f0fdfa' : '#f8fafc',
                    borderBottom: expandido ? '1px solid #99f6e4' : 'none',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: expandido ? '#ccfbf1' : '#eef2f6' },
                  }}>
                  <IconButton size="small" sx={{
                    transition: 'transform 0.18s',
                    transform: expandido ? 'rotate(0deg)' : 'rotate(-90deg)',
                    color: '#0d9488',
                  }}>
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                    {g.label}
                  </Typography>
                  <Chip size="small"
                    label={`${g.kpis.num} ${g.kpis.num === 1 ? 'reserva' : 'reservas'}`}
                    sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700, fontSize: 11, height: 22 }} />
                  <Chip size="small"
                    label={`${g.kpis.numCompl} completadas`}
                    sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: 11, height: 22,
                          display: { xs: 'none', sm: 'inline-flex' } }} />
                  <Typography sx={{
                    fontSize: 13, fontWeight: 800, color: '#0d9488',
                    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                    fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                  }}>
                    {formatPrecio(g.kpis.facturado)}
                  </Typography>
                </Box>

                {/* Contenido del mes — tabla sin buscador local */}
                <Collapse in={expandido} timeout={180} unmountOnExit>
                  <Box sx={{ p: 1.25, pt: 0.75 }}>
                    <ReservacionesTable
                      rows={g.reservas}
                      loading={false}
                      onRowClick={r => setDetalleId(r.id)}
                      defaultSortBy="salida_fecha"
                      defaultSortDir="desc"
                      hideSearch
                      emptyTitle=""
                      emptyMessage=""
                    />
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      )}

      {detalleId && (
        <ReservaSlideOver reservaId={detalleId}
          onClose={() => setDetalleId(null)} onUpdated={refetch} />
      )}

      {/* Modal de snapshot histórico — auditoría de creación, timeline,
          pagos y vouchers. Reutilizable para cualquier reserva por código. */}
      <HistorialReservaModal
        codigo={historialCodigo}
        open={!!historialCodigo}
        onClose={() => setHistorialCodigo(null)}
      />
    </Box>
  );
}
